// Facility inspection/maintenance module constants + shared helpers.

export const INSP_KINDS = [
  { value: "wiederkehrende_pruefung", label: "Wiederkehrende Prüfung" },
  { value: "einmalige_pruefung", label: "Einmalige Prüfung" },
  { value: "wiederkehrende_wartung", label: "Wiederkehrende Wartung" },
  { value: "einmalige_wartung", label: "Einmalige Wartung" },
  { value: "reparatur", label: "Reparatur" },
  { value: "ausserplanmaessige_pruefung", label: "Außerplanmäßige Prüfung" },
  { value: "sonderpruefung", label: "Sonderprüfung" },
];

export const ONE_TIME_KINDS = new Set([
  "einmalige_pruefung", "einmalige_wartung", "reparatur", "ausserplanmaessige_pruefung", "sonderpruefung",
]);

export const INTERVALS = [
  { value: "woechentlich", label: "Wöchentlich" },
  { value: "monatlich", label: "Monatlich" },
  { value: "zweimonatlich", label: "Alle 2 Monate" },
  { value: "vierteljaehrlich", label: "Vierteljährlich" },
  { value: "halbjaehrlich", label: "Halbjährlich" },
  { value: "jaehrlich", label: "Jährlich" },
  { value: "zweijaehrlich", label: "Alle 2 Jahre" },
  { value: "dreijaehrlich", label: "Alle 3 Jahre" },
  { value: "benutzerdefiniert", label: "Frei definiert" },
  { value: "keine", label: "Kein Intervall (einmalig)" },
];

export const INTERVAL_UNITS = [
  { value: "tage", label: "Tage" },
  { value: "wochen", label: "Wochen" },
  { value: "monate", label: "Monate" },
  { value: "jahre", label: "Jahre" },
];

export const INSP_RESULTS = [
  { value: "bestanden", label: "Bestanden / mängelfrei" },
  { value: "bestanden_hinweise", label: "Bestanden mit Hinweisen" },
  { value: "maengel", label: "Mängel festgestellt" },
  { value: "nicht_bestanden", label: "Nicht bestanden" },
  { value: "nachpruefung", label: "Nachprüfung erforderlich" },
  { value: "abgebrochen", label: "Prüfung abgebrochen" },
];

export const INSP_PRIORITIES = [
  { value: "niedrig", label: "Niedrig" },
  { value: "normal", label: "Normal" },
  { value: "hoch", label: "Hoch" },
  { value: "kritisch", label: "Kritisch" },
];

// Unified color coding across the whole inspection module.
export const INSP_STATUS = {
  geplant: { label: "Geplant", dot: "#2563EB", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  bald_faellig: { label: "Bald fällig", dot: "#EA580C", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  ueberfaellig: { label: "Überfällig", dot: "#DC2626", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  erledigt: { label: "Erledigt", dot: "#16A34A", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  deaktiviert: { label: "Deaktiviert", dot: "#94A3B8", bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-200" },
};

export const RESULT_STYLE = {
  bestanden: "bg-green-50 text-green-700 border-green-200",
  bestanden_hinweise: "bg-lime-50 text-lime-700 border-lime-200",
  maengel: "bg-orange-50 text-orange-700 border-orange-200",
  nicht_bestanden: "bg-red-50 text-red-700 border-red-200",
  nachpruefung: "bg-amber-50 text-amber-700 border-amber-200",
  abgebrochen: "bg-slate-100 text-slate-600 border-slate-200",
};

export const MEASURE_STATUS = {
  offen: { label: "Offen", dot: "#DC2626", bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  in_bearbeitung: { label: "In Bearbeitung", dot: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  beauftragt: { label: "Beauftragt", dot: "#2563EB", bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
  wartet_dienstleister: { label: "Wartet auf Dienstleister", dot: "#8B5CF6", bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
  behoben: { label: "Behoben", dot: "#16A34A", bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  nachpruefung: { label: "Nachprüfung erforderlich", dot: "#EA580C", bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200" },
  abgeschlossen: { label: "Abgeschlossen", dot: "#0D9488", bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200" },
};
export const MEASURE_STATUS_OPTIONS = Object.entries(MEASURE_STATUS).map(([value, v]) => ({ value, label: v.label }));
export const DEFECT_RESULTS = new Set(["maengel", "nicht_bestanden", "nachpruefung"]);

import { can } from "@/lib/perms";

export const WORKSPACE_ROLE_OPTIONS = [
  { value: "admin", label: "Administrator" },
  { value: "geschaeftsleitung", label: "Geschäftsleitung" },
  { value: "leitung", label: "Einrichtungsleitung" },
  { value: "haustechnik", label: "Haustechnik" },
  { value: "mitarbeiter", label: "Mitarbeiter" },
  { value: "pruefer", label: "Prüfer / Dienstleister" },
];

export const ROLE_LABEL = {
  admin: "Administrator", geschaeftsleitung: "Geschäftsleitung", leitung: "Einrichtungsleitung", haustechnik: "Haustechnik",
  mitarbeiter: "Mitarbeiter", pruefer: "Prüfer / Dienstleister", user: "Benutzer",
};

export const canInsp = (user, action) => can(user, `insp:${action}`);

export const kindLabel = (v) => INSP_KINDS.find((k) => k.value === v)?.label || v;
export const intervalLabel = (v) => INTERVALS.find((k) => k.value === v)?.label || v;
export const resultLabel = (v) => INSP_RESULTS.find((k) => k.value === v)?.label || v;

// Client-side mirror of backend compute_next_due (preview only).
function addMonths(d, months) {
  const y = d.getFullYear();
  const m = d.getMonth() + months;
  const day = d.getDate();
  const target = new Date(y, m, 1);
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, last));
  return target;
}
const MONTHS = { monatlich: 1, zweimonatlich: 2, vierteljaehrlich: 3, halbjaehrlich: 6, jaehrlich: 12, zweijaehrlich: 24, dreijaehrlich: 36 };

export function previewNextDue(anchorIso, intervalType, value, unit) {
  if (!anchorIso || !intervalType || intervalType === "keine" || intervalType === "einmalig") return null;
  const d = new Date(anchorIso);
  if (isNaN(d)) return null;
  const fmt = (x) => x.toISOString().slice(0, 10);
  if (intervalType === "woechentlich") { d.setDate(d.getDate() + 7); return fmt(d); }
  if (MONTHS[intervalType]) return fmt(addMonths(d, MONTHS[intervalType]));
  if (intervalType === "benutzerdefiniert") {
    const v = parseInt(value || 0, 10);
    if (v <= 0) return null;
    if (unit === "tage") { d.setDate(d.getDate() + v); return fmt(d); }
    if (unit === "wochen") { d.setDate(d.getDate() + v * 7); return fmt(d); }
    if (unit === "monate") return fmt(addMonths(d, v));
    if (unit === "jahre") return fmt(addMonths(d, v * 12));
  }
  return null;
}
